/**
 * Settings → Billing address, rendered under both Personal information and
 * Payouts.
 *
 * It started out under the owner's own name, email and phone, since that is
 * where people look for "the details CoLiviq has on me". It's also rendered
 * under Payouts because owners look for it there just as often — being an
 * address that ends up on a payout-adjacent document (see below), Payouts is
 * an equally reasonable place to expect it, not a wrong one this component
 * should steer people away from.
 *
 * It is still not a preference: this edits the lessor record management
 * invoices print, so the card says so out loud — otherwise "saved" reads as a
 * settings change rather than a change to a document someone will receive.
 * Hence its own card rather than more fields in the profile form above: one
 * Save button covering both would quietly re-address invoices whenever someone
 * corrected their phone number. See useAccountBillingAddress for how the
 * target row is resolved, and why some accounts cannot edit it here at all.
 */
import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Receipt, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useAccountBillingAddress,
  useSaveAccountBillingAddress,
} from "./useAccountBillingAddress";

export function BillingAddressCard() {
  const { t } = useLanguage();
  const { data, isLoading } = useAccountBillingAddress();
  const saveMutation = useSaveAccountBillingAddress();

  const [street, setStreet] = useState("");
  const [cityPostal, setCityPostal] = useState("");
  const [dirty, setDirty] = useState(false);

  // Server value wins until the user starts typing, so a background refetch
  // can't wipe a half-typed address.
  useEffect(() => {
    if (dirty || !data) return;
    setStreet(data.address ?? "");
    setCityPostal(data.city_postal ?? "");
  }, [data, dirty]);

  const readOnly = data ? !data.editable : true;

  // `null` means the database this client talks to has no billing-address
  // function yet (migration not applied — the dev project trails LIVE). Render
  // nothing rather than an empty card that cannot save.
  if (!isLoading && data === null) return null;

  const save = () => {
    saveMutation.mutate(
      { address: street, cityPostal },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success(t("settings.billing.saved", "Billing address saved."));
        },
        onError: (error: unknown) => {
          toast.error(
            error instanceof Error
              ? error.message
              : t(
                  "settings.billing.saveFailed",
                  "Could not save the billing address."
                )
          );
        },
      }
    );
  };

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
      <CardHeader className="border-b bg-muted/10 pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
          {t("settings.billing.title", "Billing address")}
        </CardTitle>
        <CardDescription>
          {t(
            "settings.billing.descLessor",
            "Printed on the management invoices coliviq issues you."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-5">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {data?.source === "ambiguous" ? (
              <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900/90 dark:text-amber-200">
                  {t(
                    "settings.billing.ambiguous",
                    "Your account is linked to more than one owner record, so the billing address has to be changed by coliviq. Contact us and we'll update it."
                  )}
                </p>
              </div>
            ) : data?.source === "none" ? (
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/20 p-4">
                <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  {t(
                    "settings.billing.noRecord",
                    "Your owner record isn't set up yet, so there are no invoices to address. It appears here once coliviq links it."
                  )}
                </p>
              </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="billing-street">
                  {t("settings.billing.street", "Street and house number")}
                </Label>
                <AddressAutocomplete
                  id="billing-street"
                  value={street}
                  readOnly={readOnly}
                  disabled={readOnly}
                  autoComplete="billing street-address"
                  onChange={(v) => {
                    setDirty(true);
                    setStreet(v);
                  }}
                  onSelect={({ street: s, houseNumber, postalCode, city }) => {
                    setDirty(true);
                    setStreet(houseNumber ? `${s} ${houseNumber}` : s);
                    if (postalCode || city) {
                      setCityPostal(
                        [postalCode, city].filter(Boolean).join(" ")
                      );
                    }
                  }}
                  placeholder={t(
                    "settings.billing.streetPlaceholder",
                    "e.g. Musterstraße 12"
                  )}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing-city">
                  {t("settings.billing.cityPostal", "Postal code and city")}
                </Label>
                <Input
                  id="billing-city"
                  value={cityPostal}
                  readOnly={readOnly}
                  disabled={readOnly}
                  autoComplete="billing postal-code"
                  onChange={(e) => {
                    setDirty(true);
                    setCityPostal(e.target.value);
                  }}
                  placeholder={t(
                    "settings.billing.cityPostalPlaceholder",
                    "e.g. 60311 Frankfurt am Main"
                  )}
                  className="rounded-xl"
                />
              </div>
            </div>

            {data?.source === "lessor" && data.lessor_name ? (
              <p className="text-xs text-muted-foreground">
                {t("settings.billing.lessorHint", "Used on invoices issued to")}{" "}
                <span className="font-medium text-foreground">
                  {data.lessor_name}
                </span>
                .
              </p>
            ) : null}

            {!readOnly && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  className="rounded-xl"
                  onClick={save}
                  disabled={saveMutation.isPending || !dirty}
                >
                  {saveMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("settings.saving", "Saving…")}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t("owner.settings.saveChanges", "Save changes")}
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default BillingAddressCard;
